import { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  Button,
  Typography,
  useTheme,
  Grid,
  Card,
  Divider,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LanguageIcon from "@mui/icons-material/Language";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { useAuth, tokens } from "@/utilities";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import facultyService from "@/api/faculty.service";
import { facultyProps } from "@/types";
import Swal from "sweetalert2";
import ModalUpdateFaculty from "@/modals/Modal-updateFaculty";

interface Props {
  facultyId?: string;
  facultyName?: string;
}

export const DetailFacultyTab = ({
  facultyId: propFacultyId,
  facultyName: propFacultyName,
}: Props) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { user, institute } = useAuth();
  const { code, id } = useParams<{ code?: string; id?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const role = user?.userData.role;

  const fallbackFacultyId = useMemo(() => {
    const rawUid = user?.userData.uidFaculty;
    return Array.isArray(rawUid) ? rawUid[0] : rawUid;
  }, [user?.userData.uidFaculty]);

  const isIESOrSuperAdmin =
    role === "Administrador IES" || role === "Super Administrador";
  const resolvedFacultyId = propFacultyId || id || fallbackFacultyId || "";
  const instituteID =
    code || institute?.code || user?.userData?.institute || "";

  const [facultyData, setFacultyData] = useState<facultyProps | null>(null);
  const [facultyName, setIsFacultyName] = useState<string>(
    propFacultyName || location.state?.facultyName || ""
  );

  const refFirebase = `institutes/${instituteID}/faculties/${resolvedFacultyId}`;

  const fetchFaculty = useCallback(async () => {
    if (instituteID && resolvedFacultyId) {
      try {
        const data = await facultyService.getById(
          instituteID,
          resolvedFacultyId
        );
        if (data) {
          setFacultyData(data);
          if (data.name) setIsFacultyName(data.name);
        }
      } catch (error) {
        console.error("Error fetching faculty data:", error);
      }
    }
  }, [instituteID, resolvedFacultyId]);

  useEffect(() => {
    fetchFaculty();
  }, [fetchFaculty]);

  const handleDelete = () => {
    Swal.fire({
      title: "Está a punto de eliminar esta facultad",
      text: "¿Desea continuar con la operación?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#002D82",
      confirmButtonText: "Si, ¡eliminar!",
      cancelButtonText: "Cancelar",
    }).then(async (result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: "Eliminando facultad...",
          text: "Por favor espere mientras se eliminan la facultad y sus carreras asociadas.",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });
        const deleteResult =
          instituteID &&
          resolvedFacultyId &&
          (await facultyService.delete(instituteID, resolvedFacultyId));
        if (deleteResult) {
          Swal.fire(
            "¡Eliminada!",
            "Su facultad fue eliminada satisfactoriamente",
            "success"
          );
          if (role === "Super Administrador") {
            navigate("/institutes", { state: { selectedInstituteCode: instituteID } });
          } else {
            navigate("/faculties");
          }
        } else {
          Swal.fire(
            "¡Intenta nuevamente!",
            "Hubo un error al intentar eliminar la facultad",
            "error"
          );
        }
      }
    });
  };

  const displayName =
    facultyData?.name || facultyName || location.state?.facultyName || "";

  const facultyWebsite = useMemo(() => {
    if (!facultyData) return "";
    const isCustom = facultyData.contactInfo?.default === "faculty";
    const customUrl = facultyData.webSiteFaculty?.trim();
    const instUrl = facultyData.webSite?.trim() || institute?.webSite?.trim() || "";

    if (isCustom && customUrl) {
      return customUrl;
    }

    if (!facultyData.contactInfo?.default && customUrl) {
      return customUrl;
    }

    return instUrl;
  }, [facultyData, institute]);

  const formattedHref = useMemo(() => {
    if (!facultyWebsite) return "";
    if (facultyWebsite.startsWith("http://") || facultyWebsite.startsWith("https://")) {
      return facultyWebsite;
    }
    return `https://${facultyWebsite}`;
  }, [facultyWebsite]);

  const facultyEmail = useMemo(() => {
    if (!facultyData?.contactInfo) return "";
    const defaultType = facultyData.contactInfo.default || "faculty";
    const primary = facultyData.contactInfo[defaultType];
    const fallback = defaultType === "faculty" ? facultyData.contactInfo.ies : facultyData.contactInfo.faculty;
    return primary?.contactEmail?.trim() || fallback?.contactEmail?.trim() || "";
  }, [facultyData]);

  const facultyPhone = useMemo(() => {
    if (!facultyData?.contactInfo) return null;
    const defaultType = facultyData.contactInfo.default || "faculty";
    const primary = facultyData.contactInfo[defaultType];
    const fallback = defaultType === "faculty" ? facultyData.contactInfo.ies : facultyData.contactInfo.faculty;
    const phone = primary?.contactPhone || fallback?.contactPhone;
    if (!phone) return null;
    const code = primary?.phone_code_country || fallback?.phone_code_country || "+57";
    return { phone, code };
  }, [facultyData]);

  const facultyFinSupport = useMemo(() => {
    if (!facultyData?.financialSupport) {
      return institute?.financialSupportIES || null;
    }
    const defaultType = facultyData.financialSupport.default || "ies";
    const primary = facultyData.financialSupport[defaultType];
    const fallback =
      defaultType === "faculty"
        ? facultyData.financialSupport.ies || institute?.financialSupportIES
        : facultyData.financialSupport.faculty;
    return primary || fallback || null;
  }, [facultyData, institute]);

  const hasFinSupport = Boolean(facultyFinSupport?.hasFinancialSupport);

  const finWebsite = useMemo(() => {
    return facultyFinSupport?.website?.trim() || "";
  }, [facultyFinSupport]);

  const finFormattedHref = useMemo(() => {
    if (!finWebsite) return "";
    if (finWebsite.startsWith("http://") || finWebsite.startsWith("https://")) {
      return finWebsite;
    }
    return `https://${finWebsite}`;
  }, [finWebsite]);

  const finEmail = useMemo(() => {
    return facultyFinSupport?.email?.trim() || "";
  }, [facultyFinSupport]);

  const finPhone = useMemo(() => {
    if (!facultyFinSupport?.contactPhone) return null;
    const code =
      facultyFinSupport.phone_code_country &&
      facultyFinSupport.phone_code_country !== "Prefijo"
        ? facultyFinSupport.phone_code_country
        : "+57";
    return { phone: facultyFinSupport.contactPhone, code };
  }, [facultyFinSupport]);

  const finWhatsapp = useMemo(() => {
    if (!facultyFinSupport?.hasWhatsapp || !facultyFinSupport?.whatsapp) return null;
    return facultyFinSupport.whatsapp;
  }, [facultyFinSupport]);

  return (
    <Box style={{ width: "100%", padding: "20px" }}>
      {isIESOrSuperAdmin && (
        <Box sx={{ marginBottom: "20px" }}>
          <Button
            sx={{
              backgroundColor: "#3F51B5",
              color: "#f2f0f0",
              display: "flex",
              gap: "10px",
              padding: "10px 20px",
              borderRadius: "5px",
              textTransform: "none",
              fontSize: "14px",
              "&:hover": {
                backgroundColor: colors.blueAccent[500],
                cursor: "pointer",
              },
            }}
            onClick={() =>
              role === "Super Administrador"
                ? navigate("/institutes", { state: { selectedInstituteCode: instituteID } })
                : navigate("/faculties")
            }
          >
            <ArrowBackIcon />
            {role === "Super Administrador" ? "Volver" : "Volver a facultades"}
          </Button>
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", md: "center" },
          gap: "20px",
          marginBottom: "30px",
        }}
      >
        <Typography
          variant="h3"
          sx={{ color: colors.grey[100], fontWeight: "bold" }}
        >
          Facultad de {displayName}
        </Typography>

        <Box sx={{ display: "flex", gap: "15px" }}>
          <ModalUpdateFaculty
            code={instituteID}
            facultyName={displayName}
            setIsFacultyName={setIsFacultyName}
            refFirebase={refFirebase}
            onUpdateSuccess={fetchFaculty}
          />
          <Button variant="outlined" color="error" onClick={handleDelete}>
            Eliminar
          </Button>
        </Box>
      </Box>

      {/* Faculty Info Card */}
      <Card
        sx={{
          backgroundColor: colors.primary[400],
          borderRadius: "8px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          padding: "25px",
        }}
      >
        <Grid container spacing={3}>
          {facultyData?.logo && (
            <Grid item xs={12} sm={3} md={2}>
              <Box
                component="img"
                src={facultyData.logo}
                alt="Logo Facultad"
                sx={{
                  width: "100%",
                  maxHeight: "150px",
                  objectFit: "contain",
                  borderRadius: "8px",
                }}
              />
            </Grid>
          )}

          <Grid
            item
            xs={12}
            sm={facultyData?.logo ? 9 : 12}
            md={facultyData?.logo ? 10 : 12}
          >
            <Typography
              variant="h4"
              color={colors.grey[100]}
              sx={{ fontWeight: "bold", mb: 1.5 }}
            >
              {displayName}
            </Typography>

            {facultyData?.description ? (
              <Typography
                variant="body1"
                color={colors.grey[200]}
                sx={{ mb: 2, whiteSpace: "pre-wrap", fontSize: "16px" }}
              >
                {facultyData.description}
              </Typography>
            ) : (
              <Typography
                variant="body2"
                color={colors.grey[400]}
                sx={{ mb: 2, fontStyle: "italic" }}
              >
                Sin descripción registrada para la facultad.
              </Typography>
            )}

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={2}>
              {facultyWebsite && (
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <LanguageIcon color="primary" />
                    <Typography variant="body2" color={colors.grey[100]}>
                      <b>Sitio web:</b>{" "}
                      <a
                        href={formattedHref}
                        target="_blank"
                        rel="noreferrer"
                        style={{ color: colors.blueAccent[300] }}
                      >
                        {facultyWebsite}
                      </a>
                    </Typography>
                  </Box>
                </Grid>
              )}

              {facultyEmail && (
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <EmailIcon color="primary" />
                    <Typography variant="body2" color={colors.grey[100]}>
                      <b>Correo:</b> {facultyEmail}
                    </Typography>
                  </Box>
                </Grid>
              )}

              {facultyPhone && (
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PhoneIcon color="primary" />
                    <Typography variant="body2" color={colors.grey[100]}>
                      <b>Teléfono:</b> {facultyPhone.code} {facultyPhone.phone}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Typography
              variant="h5"
              color={colors.grey[100]}
              sx={{ fontWeight: "bold", mb: 1.5 }}
            >
              Apoyo Financiero
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <MonetizationOnIcon color="primary" />
                  <Typography variant="body2" color={colors.grey[100]}>
                    <b>Tiene Apoyo Financiero:</b>{" "}
                    {hasFinSupport ? "Sí" : "No"}
                  </Typography>
                </Box>
              </Grid>

              {hasFinSupport && finWebsite && (
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <LanguageIcon color="primary" />
                    <Typography variant="body2" color={colors.grey[100]}>
                      <b>Sitio web:</b>{" "}
                      <a
                        href={finFormattedHref}
                        target="_blank"
                        rel="noreferrer"
                        style={{ color: colors.blueAccent[300] }}
                      >
                        {finWebsite}
                      </a>
                    </Typography>
                  </Box>
                </Grid>
              )}

              {hasFinSupport && finEmail && (
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <EmailIcon color="primary" />
                    <Typography variant="body2" color={colors.grey[100]}>
                      <b>Correo:</b> {finEmail}
                    </Typography>
                  </Box>
                </Grid>
              )}

              {hasFinSupport && finPhone && (
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PhoneIcon color="primary" />
                    <Typography variant="body2" color={colors.grey[100]}>
                      <b>Teléfono:</b> {finPhone.code} {finPhone.phone}
                    </Typography>
                  </Box>
                </Grid>
              )}

              {hasFinSupport && finWhatsapp && (
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <WhatsAppIcon sx={{ color: "#25D366" }} />
                    <Typography variant="body2" color={colors.grey[100]}>
                      <b>WhatsApp:</b> {finWhatsapp}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Card>
    </Box>
  );
};
